// ---------------------------------------------------------------------------
// Reading somebody else's turf, over the network.
//
// The canvasser screens read the device store, which only ever holds turf the
// signed-in user is assigned (offline/sync.ts, `myTurfIds`). That is the right
// scope for a phone in a pocket and the wrong one for an organizer at a desk
// who wants to see what a volunteer is walking into -- before this, the only
// way to open a turf in the detail view was to assign yourself to it, which
// puts you on the roster and pulls the whole turf onto your device.
//
// So: the same rows, fetched for one turf, without touching IndexedDB. RLS
// already allows it -- `is_organizer()` is the first disjunct of every read
// policy in 20260915000100_rls_evaluate_once.sql -- and it deliberately does
// NOT allow the matching inserts, which is why everything this feeds is
// read-only (see TurfDataProvider's `readOnly`).
//
// Column lists are shared with the pull rather than restated, so a column
// added for canvassers cannot go missing here.
// ---------------------------------------------------------------------------

import type {
  Household,
  Person,
  PersonContact,
  Turf,
  Visit,
} from '@yucanvas/shared';
import { fetchAllPages } from '../lib/paginate';
import { supabase } from '../lib/supabase';
import {
  CONTACT_COLUMNS,
  HOUSEHOLD_COLUMNS,
  NOTE_COLUMNS,
  PERSON_COLUMNS,
  VISIT_COLUMNS,
} from '../offline/columns';
import type { LocalHouseNote } from '../offline/db';

/** A visit with the canvasser resolved, which the device store cannot do. */
export type ReviewVisit = Visit & { canvasser_name: string | null };

export interface TurfReview {
  turf: Turf;
  households: Household[];
  visits: ReviewVisit[];
  people: Person[];
  contacts: PersonContact[];
  /**
   * Shaped like the device's own notes so the sheet renders one kind of row.
   * `synced` is 1 by construction: these came from the server.
   */
  notes: LocalHouseNote[];
}

/** Drops the `!inner` join columns a scoped select drags along. */
function unjoin<Row extends object, Key extends string>(
  rows: readonly Row[],
  ...keys: Key[]
): Omit<Row, Key>[] {
  return rows.map((row) => {
    const copy = { ...row } as Record<string, unknown>;
    for (const key of keys) delete copy[key];
    return copy as Omit<Row, Key>;
  });
}

/**
 * Everything the turf screens draw, for one turf, in five round trips.
 *
 * Each query is scoped by `turf_id` through an inner join rather than a list
 * of household ids, for the reason the pull gives: a turf holds thousands of
 * them and the id list would not fit in a URL. Ordered by `id` because
 * fetchAllPages walks offsets, and an unordered offset scan can repeat or skip
 * rows between pages.
 */
export async function fetchTurfReview(turfId: string): Promise<TurfReview> {
  const { data: turf, error: turfError } = await supabase
    .from('turfs')
    .select('*')
    .eq('id', turfId)
    .single();
  if (turfError) throw new Error(turfError.message);

  const [households, visitRows, people, contactRows, noteRows] = await Promise.all([
    fetchAllPages((from, to) =>
      supabase
        .from('households')
        .select(HOUSEHOLD_COLUMNS)
        .eq('turf_id', turfId)
        .eq('active', true)
        .order('id')
        .range(from, to),
    ) as unknown as Promise<Household[]>,

    fetchAllPages((from, to) =>
      supabase
        .from('visits')
        // profiles(name) is what lets the read-only sheet say who knocked. A
        // canvasser's own device has no roster and shows the outcome alone.
        .select(`${VISIT_COLUMNS},households!inner(turf_id),profiles(name)`)
        .eq('households.turf_id', turfId)
        .order('id')
        .range(from, to),
    ) as unknown as Promise<
      (Visit & { households?: unknown; profiles?: { name?: string } | null })[]
    >,

    fetchAllPages((from, to) =>
      supabase
        .from('people')
        .select(`${PERSON_COLUMNS},households!inner(turf_id)`)
        .eq('households.turf_id', turfId)
        .order('id')
        .range(from, to),
    ) as unknown as Promise<(Person & { households?: unknown })[]>,

    fetchAllPages((from, to) =>
      supabase
        .from('person_contacts')
        .select(`${CONTACT_COLUMNS},visits!inner(household_id,households!inner(turf_id))`)
        .eq('visits.households.turf_id', turfId)
        .order('id')
        .range(from, to),
    ) as unknown as Promise<(PersonContact & { visits?: unknown })[]>,

    fetchAllPages((from, to) =>
      supabase
        .from('household_notes')
        .select(`${NOTE_COLUMNS},households!inner(turf_id),profiles(name)`)
        .eq('households.turf_id', turfId)
        .order('id')
        .range(from, to),
    ) as unknown as Promise<
      (Record<string, unknown> & { profiles?: { name?: string } | null })[]
    >,
  ]);

  return {
    turf: turf as Turf,
    households,
    visits: visitRows.map(({ households: _turf, profiles: author, ...visit }) => {
      void _turf;
      return { ...(visit as Visit), canvasser_name: author?.name ?? null };
    }),
    people: unjoin(people, 'households') as Person[],
    contacts: unjoin(contactRows, 'visits') as PersonContact[],
    notes: noteRows.map(({ households: _turf, profiles: author, ...note }) => {
      void _turf;
      return {
        ...(note as unknown as LocalHouseNote),
        author_name: author?.name ?? null,
        synced: 1 as const,
        attempts: 0,
        last_error: null,
      };
    }),
  };
}
